import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Text,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useEffect, useState } from 'react';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useUpdateCampaignFrequencyCap } from '../../organization/hooks/useUpdateCampaignFrequencyCap';

export default function CampaignFrequencyCapCard() {
    const { selectedOrganization } = useSelectedOrganization();
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();

    const [capValue, setCapValue] = useState<number>(
        selectedOrganization?.campaignFrequencyCapPerWeek ?? 0,
    );

    useEffect(() => {
        setCapValue(selectedOrganization?.campaignFrequencyCapPerWeek ?? 0);
    }, [selectedOrganization?.id, selectedOrganization?.campaignFrequencyCapPerWeek]);

    const { mutateAsync: updateCap, isLoading } = useUpdateCampaignFrequencyCap(
        selectedOrganization?.id || '',
    );

    const handleSave = async () => {
        try {
            await updateCap({ campaignFrequencyCapPerWeek: capValue });
            toast({
                title: translate('organization.campaign_frequency_cap_updated'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error: any) {
            toast({
                title: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    if (!selectedOrganization) return null;

    const isDirty = capValue !== (selectedOrganization.campaignFrequencyCapPerWeek ?? 0);

    return (
        <Card p="20px">
            <Text color={textColor} fontSize="16px" fontWeight="700" mb={2}>
                {translate('organization.campaign_frequency_cap')}
            </Text>
            <Text color="gray.500" fontSize="sm" mb={4}>
                {translate('organization.campaign_frequency_cap_description')}
            </Text>
            <Flex align="end" gap={3}>
                <FormControl maxW="200px">
                    <FormLabel fontSize="sm">
                        {translate('organization.campaign_frequency_cap_per_week')}
                    </FormLabel>
                    <NumberInput
                        min={0}
                        max={100}
                        value={capValue}
                        onChange={(_, val) => setCapValue(isNaN(val) ? 0 : val)}
                    >
                        <NumberInputField />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                </FormControl>
                <Box>
                    <Text fontSize="xs" color="gray.400" mb={1}>
                        {capValue === 0
                            ? translate('organization.campaign_frequency_cap_no_limit')
                            : `${capValue} / ${translate('organization.campaign_frequency_cap_per_week')}`}
                    </Text>
                </Box>
                <Button
                    colorScheme="brand"
                    size="sm"
                    onClick={handleSave}
                    isLoading={isLoading}
                    isDisabled={!isDirty}
                >
                    {translate('save')}
                </Button>
            </Flex>
        </Card>
    );
}
